import Typography from "@mui/material/Typography";
import Dot from "components/Dot";
import type { LinePointData } from "components/charting/types/Chart.types";
import useAuthorName from "hooks/useAuthorName";
import useUnitParser from "hooks/useUnitParser";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ExerciseMetric } from "types";
import { MetadataType } from "types/metadata";
import useOneRepMaxChartColor from "../../../useOneRepMaxChartColor";
import { OneRepMaxTooltipCardRowLabel } from "./OneRepMaxTooltipCardRow.components";
import useUnitNotation from "hooks/useUnitNotation";

interface OneRepMaxTooltipCardRowProps {
  point: LinePointData;
  weightMetric?: ExerciseMetric;
}

function OneRepMaxTooltipCardRow(props: OneRepMaxTooltipCardRowProps) {
  const { point, weightMetric } = props;
  const creatorId = point.metadataType === MetadataType.ONE_REP_MAX ? (point.measurement.creatorId ?? null) : null;
  const creatorName = useAuthorName(creatorId);
  const parseUnit = useUnitParser();
  const { unitNotation } = useUnitNotation();
  const getLineColor = useOneRepMaxChartColor();
  const { t } = useTranslation();

  const color = useMemo(() => getLineColor(point.variant), [getLineColor, point.variant]);

  const label = useMemo(() => {
    if (!point.variant) return t("charting.one_rep_max_tooltip_source", { context: point.metadataType });
    return t("charting.one_rep_max_tooltip_source_variant", {
      context: point.metadataType,
      variant: t("measures.variant", { context: point.variant }),
    });
  }, [point.metadataType, point.variant, t]);

  const value = useMemo(() => {
    if (point.metadataType === MetadataType.LOAD_VELOCITY_PROFILE) {
      const { min, max } = point.measurement.predictedOneRepMaxRange;
      return t("charting.one_rep_max_tooltip_range", {
        min: Math.round(parseUnit(min, weightMetric).value),
        max: `${Math.round(parseUnit(max, weightMetric).value)} ${unitNotation(weightMetric)}`,
      });
    }
    const value = Math.round(parseUnit(point.y, weightMetric).value);
    const unit = unitNotation(weightMetric);
    return `${value} ${unit}`;
  }, [parseUnit, point, t, unitNotation, weightMetric]);

  return (
    <div>
      <OneRepMaxTooltipCardRowLabel>
        <Dot bgcolor={color} size={8} />
        <Typography variant="TextXS">{label}</Typography>
      </OneRepMaxTooltipCardRowLabel>
      <Typography variant="TextXS" sx={{ fontWeight: "fontWeightBold" }}>
        {value}
      </Typography>
      {creatorName ? (
        <Typography variant="TextXS" color="grey.100">
          {t("charting.one_rep_max_tooltip_added_by", { name: creatorName })}
        </Typography>
      ) : null}
    </div>
  );
}

export default memo(OneRepMaxTooltipCardRow);
